import React, { memo, ReactElement, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { Table } from 'antd'
import styled from 'styled-components'
import { v4 as uuidv4 } from 'uuid'

import { TemporaryDataType, TemporarySetDataType } from './useTemporaryDataState'
import { runColumns } from './static-data-columns'
import { EditableCell, EditableRow } from './editable-table-utils'
import TemporaryTubularTypeTable from './TemporaryTubularTypeTable'
import TemporaryCementPlugTable from './TemporaryCementPlugTable'
import { fetchEntityOptions } from '../../../widgets/static-data/api/api'
import { CustomDateTimePicker } from './components/CustomDateTimePicker'
import { emptyRun } from '../utils/emptyEntities'
import { CustomTableHeader } from './components/CustomTableHeader'
import { CustomExpandRowIcon } from './components/CustomExpandRowIcon'
import { saveAllTemporaryData } from '../importer-api'
import { deleteEntityAndItsChildren, deletedCascadeEntities } from '../utils/actions'
import { StaticDataImporterContext } from '../context/StaticDataImporterContext'
import { getSelectedRowKeys, useSelectAllRows } from '../hooks/useSelectAllRows'
import { useNavigateToNewRow } from '../hooks/useNavigateToNewRow'
import { Key, RowSelectionType } from 'antd/lib/table/interface'
import { useMoveEntityRowToAnotherEntityRowParent } from '../hooks/useMoveEntityRowToAnotherEntityRowParent'

type EditableTableProps = Parameters<typeof Table>[0]

type ColumnTypes = Exclude<EditableTableProps['columns'], undefined>

const TemporaryRunTable = memo(
    ({
        parent,
        data,
        setData,
        localErrors,
        removeErrorWhenCheckedRowIsRemoved
    }: {
        parent: SectionDto
        data: TemporaryDataType
        setData: TemporarySetDataType
        localErrors: LocalErrorsTypes[]
        removeErrorWhenCheckedRowIsRemoved: (value: TemporaryDataType) => void
    }): ReactElement => {
        const [optionsOfRunEntity, setOptionsOfRunEntity] = useState(null)
        const [startDate, setStartDate] = useState<Record<number, Date | string>>(null)
        const [endDate, setEndDate] = useState<Record<number, Date | string>>(null)

        const {
            checkedRows,
            setCheckedRows,
            formatCheckedRows,
            setIsValidToTransfer,
            updateValueFromCheckedEntity,
            expandedRowKeys,
            setExpandedRowKeysByEntity
        } = useContext(StaticDataImporterContext)

        const { selectAllRows } = useSelectAllRows()

        const { goToNewRow, classNameNewRow } = useNavigateToNewRow()
        const { moveRowToAnotherParent } = useMoveEntityRowToAnotherEntityRowParent()

        const runs = useMemo(
            () =>
                data.runs
                    .filter(run => run.parent == parent.nodeId)
                    .map(r => ({
                        ...r,
                        key: String(r.rowId),
                        extendLastTubular: r?.extendLastTubular ?? false,
                        tubularsIntervalPerTime: r?.tubularsIntervalPerTime ?? false,
                        parent: parent.nodeId
                    })),
            [data.runs, parent.nodeId]
        )

        useEffect(() => {
            fetchEntityOptions('run').then(data => setOptionsOfRunEntity(data))

            const newStartDate: Record<number, Date | string> = {}
            const newEndDate: Record<number, Date | string> = {}
            for (const run of data.runs) {
                newStartDate[run.rowId] = run.start ? new Date(run.start) : ''
                newEndDate[run.rowId] = run.end ? new Date(run.end) : ''
            }
            setStartDate(newStartDate)
            setEndDate(newEndDate)
        }, []) // eslint-disable-line react-hooks/exhaustive-deps

        // Functions
        const handleEditData = useCallback(
            (entity: EntityDto[], row: EntityDto, setter: TemporarySetDataType): void => {
                const newData = [...entity]
                const index = newData.findIndex(item => String(row.rowId) === String(item.rowId))
                const item = newData[index]
                if (item) {
                    saveAllTemporaryData(row, 'RUN').then(saves => {
                        row.rowId = saves[0].id
                        row.key = saves[0].id
                        newData.splice(index, 1, {
                            ...item,
                            ...row
                        })
                        setter.setRuns(newData as RunDto[])
                        setIsValidToTransfer(false)
                        updateValueFromCheckedEntity(`runs=>${row.parent}`, row)
                    })
                }
            },
            [setIsValidToTransfer, updateValueFromCheckedEntity]
        )

        const handleEditSave = useCallback(
            (row: EntityDto): void => {
                handleEditData(data.runs, row, setData)
            },
            [data.runs, handleEditData, setData]
        )

        const setRowStartDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.runs]
                const currentRow = newData.find(row => String(row.rowId) === String(rowId))
                if (currentRow) {
                    currentRow.start = newDate ? new Date(newDate).getTime() : null
                    handleEditSave(currentRow)
                }
                // Changing startDate state
                const newStartDate = { ...startDate }
                newStartDate[rowId] = newDate
                setStartDate(newStartDate)
            },
            [data.runs, handleEditSave, startDate]
        )

        const setRowEndDate = useCallback(
            (rowId: number, newDate: Date): void => {
                // Changing entity row
                const newData = [...data.runs]
                const currentRow = newData.find(row => String(row.rowId) === String(rowId))
                if (currentRow) {
                    currentRow.end = newDate ? new Date(newDate).getTime() : null
                    handleEditSave(currentRow)
                }
                // Changing endDate state
                const newEndDate = { ...endDate }
                newEndDate[rowId] = newDate
                setEndDate(newEndDate)
            },
            [data.runs, endDate, handleEditSave]
        )

        const handleAddNewRow = useCallback((): void => {
            const oldEntity = [...data.runs]

            const newEntity: RunDto = {
                ...emptyRun,
                key: undefined,
                frontParentUuid: parent.frontUuid,
                parent: parent.nodeId,
                nodeId: Date.now(),
                rowId: undefined,
                frontUuid: uuidv4()
            }

            saveAllTemporaryData(newEntity, 'RUN').then(saves => {
                newEntity.rowId = saves[0].id
                newEntity.key = saves[0].id
                newEntity.newRow = true
                oldEntity.push(newEntity)
                setData.setRuns(oldEntity)
                goToNewRow()
            })
        }, [data.runs, goToNewRow, parent.frontUuid, parent.nodeId, setData])

        const handleDelete = useCallback(
            (record: RunDto): void => {
                const newData = data.runs.filter(item => String(item.rowId) !== String(record.rowId))
                const newCheckedRows = deletedCascadeEntities(checkedRows, record)

                deleteEntityAndItsChildren(record.rowId).then(() => {
                    const newCheckedToTransfer = formatCheckedRows(newCheckedRows)

                    setData.setRuns(newData)
                    setCheckedRows(newCheckedRows)
                    removeErrorWhenCheckedRowIsRemoved(newCheckedToTransfer)
                })
            },
            [checkedRows, data.runs, formatCheckedRows, removeErrorWhenCheckedRowIsRemoved, setCheckedRows, setData]
        )

        const handleBooleanCell = useCallback(
            (record: EntityDto, key: string): void => {
                const newData = [...data.runs]
                const currentRow = newData.find(row => String(row.rowId) === String(record.rowId))
                currentRow[key] = !record[key]
                handleEditSave(currentRow)
            },
            [data.runs, handleEditSave]
        )

        // Element components
        const handleCalendarElementStartDate = useCallback(
            (record: EntityDto): ReactElement => {
                if (!startDate) return null

                return (
                    <CustomDateTimePicker
                        date={startDate[record.rowId] as string}
                        setDate={setRowStartDate}
                        rowId={record.rowId}
                    />
                )
            },
            [setRowStartDate, startDate]
        )

        const handleCalendarElementEndDate = useCallback(
            (record: EntityDto): ReactElement => {
                if (!endDate) return null

                return (
                    <CustomDateTimePicker
                        date={endDate[record.rowId] as string}
                        setDate={setRowEndDate}
                        rowId={record.rowId}
                    />
                )
            },
            [endDate, setRowEndDate]
        )

        const handleRadioButtonElement = useCallback(
            (record: EntityDto, columnName: string): ReactElement => {
                return (
                    <input
                        checked={record[columnName]}
                        onChange={() => handleBooleanCell(record, columnName)}
                        type="checkbox"
                    />
                )
            },
            [handleBooleanCell]
        )

        const components = useMemo(
            () => ({
                body: {
                    row: EditableRow,
                    cell: EditableCell
                }
            }),
            []
        )

        const runCols = useMemo(
            () =>
                runColumns(
                    handleDelete,
                    handleEditSave,
                    optionsOfRunEntity,
                    handleCalendarElementStartDate,
                    handleCalendarElementEndDate,
                    handleRadioButtonElement,
                    (record: EntityDto, selectedParent: number) =>
                        moveRowToAnotherParent(record, selectedParent, handleEditSave),
                    data.sections,
                    localErrors
                ),
            [
                data.sections,
                handleCalendarElementEndDate,
                handleCalendarElementStartDate,
                handleDelete,
                handleEditSave,
                handleRadioButtonElement,
                localErrors,
                moveRowToAnotherParent,
                optionsOfRunEntity
            ]
        )

        const expandedRowRender = useCallback(
            (record: RunDto): ReactElement => {
                return (
                    <>
                        <TemporaryTubularTypeTable
                            parent={record as RunDto}
                            data={data}
                            setData={setData}
                            localErrors={localErrors}
                            removeErrorWhenCheckedRowIsRemoved={removeErrorWhenCheckedRowIsRemoved}
                        />
                        <TemporaryCementPlugTable
                            parent={record as RunDto}
                            data={data}
                            setData={setData}
                            localErrors={localErrors}
                            removeErrorWhenCheckedRowIsRemoved={removeErrorWhenCheckedRowIsRemoved}
                        />
                    </>
                )
            },
            // eslint-disable-next-line react-hooks/exhaustive-deps
            [data, localErrors, removeErrorWhenCheckedRowIsRemoved, setData]
        )

        const expandIcon = useCallback(({ expanded, onExpand, record }): ReactElement => {
            return <CustomExpandRowIcon expanded={expanded} onExpand={onExpand} record={record as RunDto} />
        }, [])

        const title = useCallback((): ReactElement => {
            return <CustomTableHeader title="runs" handleAddNewRow={handleAddNewRow} />
        }, [handleAddNewRow])

        const rowSelection = useMemo(
            () => ({
                selectedRowKeys: getSelectedRowKeys(checkedRows, 'runs', parent),
                type: 'checkbox' as RowSelectionType,
                onChange: (selectedRowsKey: React.Key[], selectedRows: RunDto[]) => {
                    selectAllRows({
                        data,
                        selectedRows,
                        parentNodeId: parent.nodeId,
                        type: 'runs'
                    })
                },
                onSelect: (record: EntityDto, selected: boolean, selectedRows: RunDto[], nativeEvent: Event) => {
                    selectAllRows({
                        data,
                        parentNodeId: parent.nodeId,
                        type: 'runs',
                        selectedRows,
                        target: record,
                        selected,
                        nativeEvent
                    })
                }
            }),
            [checkedRows, data, parent, selectAllRows]
        )

        const handleExpandedRowsChange = useCallback(
            (keys: React.Key[]): void => {
                setExpandedRowKeysByEntity('runs', keys as Key[])
            },
            [setExpandedRowKeysByEntity]
        )

        const expandable = useMemo(
            () => ({
                expandedRowRender,
                expandIcon,
                expandedRowKeys: expandedRowKeys?.runs,
                onExpandedRowsChange: handleExpandedRowsChange,
                defaultExpandAllRows: false
            }),
            [expandIcon, expandedRowKeys?.runs, expandedRowRender, handleExpandedRowsChange]
        )

        return (
            <TableContainer>
                <Table
                    title={title}
                    size="small"
                    components={components}
                    rowClassName={(d: EntityDto) => (d?.newRow ? `editable-row ${classNameNewRow}` : 'editable-row')}
                    bordered
                    dataSource={runs}
                    columns={runCols as ColumnTypes}
                    pagination={false}
                    rowSelection={rowSelection}
                    expandable={expandable}
                />
            </TableContainer>
        )
    }
)

TemporaryRunTable.displayName = 'TemporaryRunTable'

export default TemporaryRunTable

const TableContainer = styled.main``
